import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, DatePicker, Input, Space } from 'antd';
import {
  ECellValueType,
  ExcelTableColDef,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { useMemo, useEffect, useState, useCallback } from 'react';
import ExcelTable from 'components/ui/excel-table';
import ExportV2Icon from 'assets/icons/export-v2';
import usePagination from 'hooks/usePagination';
import FilterButton from 'components/ui/filter-button';
import { SortedSelect } from 'components/ui/sorted-select';
import SearchIcon from 'assets/icons/search';
import { ClaimsPaymentRow, EPaymentStatus } from './types';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import {
  claimPaymentsApi,
  insuredApi,
  binderApi,
  reinsuredApi,
  brokerApi,
} from 'services/apis';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';
import dayjs from 'dayjs';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import { InsuredResponseDtoStatusEnum } from 'libs/pinnacle-sdk';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ClaimsPaymentsTable: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { claimsPaymentsTable: savedFilters, setClaimsPaymentsTableFilters } =
    useDashboardFiltersStore();
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(20);
  const [isFilterOpen, setIsFilterOpen] = useState(savedFilters.isFilterOpen);
  const [searchText, setSearchText] = useState('');
  const [inputText, setInputText] = useState('');

  // Restore saved filters to form
  useEffect(() => {
    form.setFieldsValue({
      dateRange: savedFilters.selectedTransactionDateRange
        ? [
            dayjs(savedFilters.selectedTransactionDateRange[0]),
            dayjs(savedFilters.selectedTransactionDateRange[1]),
          ]
        : undefined,
      insured: savedFilters.selectedInsureds,
      binder: savedFilters.selectedBinders,
      reinsured: savedFilters.selectedReinsureds,
      broker: savedFilters.selectedBrokers,
      paymentStatus: savedFilters.selectedPaymentStatuses,
      bankAccount: savedFilters.selectedBankAccountNumbers,
    });
  }, []);

  // Watch form values for filtering
  const dateRange = Form.useWatch('dateRange', form);
  const selectedInsured = Form.useWatch('insured', form);
  const selectedBinder = Form.useWatch('binder', form);
  const selectedReinsured = Form.useWatch('reinsured', form);
  const selectedBroker = Form.useWatch('broker', form);
  const selectedPaymentStatus = Form.useWatch('paymentStatus', form);
  const selectedBankAccount = Form.useWatch('bankAccount', form);

  // Save filters to store whenever they change
  useEffect(() => {
    setClaimsPaymentsTableFilters({
      selectedInsureds: selectedInsured,
      selectedBinders: selectedBinder,
      selectedReinsureds: selectedReinsured,
      selectedBrokers: selectedBroker,
      selectedPaymentStatuses: selectedPaymentStatus,
      selectedBankAccountNumbers: selectedBankAccount,
      selectedTransactionDateRange: dateRange
        ? [dateRange[0]?.toDate(), dateRange[1]?.toDate()]
        : undefined,
      isFilterOpen,
    });
  }, [
    selectedInsured,
    selectedBinder,
    selectedReinsured,
    selectedBroker,
    selectedPaymentStatus,
    selectedBankAccount,
    dateRange,
    isFilterOpen,
    setClaimsPaymentsTableFilters,
  ]);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [
    dateRange,
    selectedInsured,
    selectedBinder,
    selectedReinsured,
    selectedBroker,
    selectedPaymentStatus,
    selectedBankAccount,
    searchText,
    resetPagination,
  ]);

  // Payment status options from enum
  const paymentStatusOptions = useMemo(
    () =>
      Object.values(EPaymentStatus).map((status) => ({
        label: status,
        value: status,
      })),
    []
  );

  const handleSearchTrigger = useCallback(() => {
    setSearchText(inputText);
  }, [inputText]);

  // Fetch claim payments data
  const {
    data: { items: claimPaymentsData, pagination: { total } } = {
      items: [],
      pagination: { total: 0 },
    },
    isLoading,
  } = useQuery(
    [
      EQueryKeys.CLAIM_PAYMENTS,
      currentPage,
      pageSize,
      selectedInsured,
      selectedBinder,
      selectedReinsured,
      selectedBroker,
      selectedPaymentStatus,
      selectedBankAccount,
      dateRange,
      searchText,
    ],
    async () => {
      const result = await claimPaymentsApi.filterClaimPayments({
        page: currentPage,
        pageSize,
        filterClaimPaymentsQueryDto: {
          insureds: selectedInsured?.length ? selectedInsured : undefined,
          binders: selectedBinder?.length ? selectedBinder : undefined,
          reinsureds: selectedReinsured?.length ? selectedReinsured : undefined,
          brokers: selectedBroker?.length ? selectedBroker : undefined,
          paymentStatuses: selectedPaymentStatus?.length
            ? selectedPaymentStatus
            : undefined,
          bankAccountNumbers: selectedBankAccount?.length
            ? selectedBankAccount
            : undefined,
          transactionDateFrom: dateRange?.[0]?.startOf('day').toDate(),
          transactionDateTo: dateRange?.[1]?.endOf('day').toDate(),
          search: searchText || undefined,
          includeInsured: true,
          includeBinder: true,
          includeReinsured: true,
          includeBroker: true,
          includeBankAccount: true,
        },
      });
      return result;
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'transactionDate',
        headerName: t('claimsPayments.transactionDate'),
        minWidth: 140,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'transactionId',
        headerName: t('claimsPayments.transactionId'),
        minWidth: 130,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'insured',
        headerName: t('claimsPayments.insured'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'binder',
        headerName: t('claimsPayments.binder'),
        minWidth: 100,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'reinsured',
        headerName: t('claimsPayments.reinsured'),
        minWidth: 120,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'broker',
        headerName: t('claimsPayments.broker'),
        minWidth: 100,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'claimsPaymentSource',
        headerName: t('claimsPayments.claimsPaymentSource'),
        minWidth: 160,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'fromBankAccount',
        headerName: t('claimsPayments.fromBankAccount'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'settlementCurrency',
        headerName: t('claimsPayments.settlementCurrency'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'thisPaymentOurShare',
        headerName: t('claimsPayments.thisPaymentOurShare'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'settlementExchangeRate',
        headerName: t('claimsPayments.settlementExchangeRate'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'paymentStatus',
        headerName: t('claimsPayments.paymentStatus'),
        minWidth: 130,
        flex: 1,
        resizable: true,
        editable: false,
      },
    ],
    [t]
  );

  // Map API data to row format
  const rowData: ClaimsPaymentRow[] = useMemo(() => {
    return claimPaymentsData.map((payment, index) => ({
      id: pageSize * (currentPage - 1) + index + 1,
      claimPaymentId: payment.claimPaymentId,
      transactionDate: dayjs(payment.transactionDate).format('DD/MM/YYYY'),
      transactionId: payment.transactionId,
      insured: payment.insured?.name ?? '',
      binder: payment.binder?.binderName ?? '',
      reinsured: payment.reinsured?.name ?? '',
      broker: payment.broker?.name ?? '',
      claimsPaymentSource: payment.claimsPaymentSource,
      fromBankAccount:
        payment.bankAccount?.accountName ?? payment.bankAccountNumber,
      settlementCurrency: payment.settlementCurrency,
      thisPaymentOurShare: payment.thisPaymentOurShare ?? '0',
      settlementExchangeRate: payment.settlementExchangeRate,
      paymentStatus: payment.paymentStatus,
    }));
  }, [claimPaymentsData, currentPage, pageSize]);

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Form form={form} className="w-full">
        <div className="flex items-center justify-between mb-6">
          <Title level={4} className="ml-4">
            {t('claimsPayments.title')}
          </Title>

          <div className="flex gap-2">
            <FilterButton
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
            <button className="flex items-center gap-2 px-6 py-2 shadow-md bg-[#1d67b6] text-white rounded-lg hover:bg-[#1d67b6]/90 transition-colors">
              <ExportV2Icon />
              {t('general.export')}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-4 flex items-center gap-2 ml-4">
          <Input
            placeholder={t('claimsPayments.searchPlaceholder')}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="flex-grow py-[8px] max-w-md"
            prefix={<SearchIcon />}
            onPressEnter={handleSearchTrigger}
          />
        </div>

        {isFilterOpen && (
          <Space direction="horizontal" wrap className="mb-4 ml-4">
            <Form.Item name="dateRange" className="w-[280px] mb-0">
              <RangePicker
                format="DD/MM/YYYY"
                size="large"
                placeholder={[t('general.startDate'), t('general.endDate')]}
                allowClear
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="insured" className="min-w-[200px] mb-0">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('claimsPayments.selectInsured')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await insuredApi.filterInsureds({
                    page,
                    pageSize,
                    insuredFilterQueryDto: {
                      searchText,
                      statuses: [InsuredResponseDtoStatusEnum.Active],
                    },
                  });
                  return {
                    data: items.map((insured) => ({
                      label: insured.name,
                      value: insured.insuredId,
                    })),
                  };
                }}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                queryKey="claimsPaymentsInsuredLazyLoadingSelect"
              />
            </Form.Item>

            <Form.Item name="binder" className="min-w-[180px] mb-0">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('claimsPayments.selectBinder')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await binderApi.filterBinders({
                    binderFilterQueryDto: { searchText },
                    page,
                    pageSize,
                  });
                  return {
                    data: items.map((binder) => ({
                      label: binder.binderName,
                      value: binder.binderId,
                    })),
                  };
                }}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                queryKey="claimsPaymentsBinderLazyLoadingSelect"
              />
            </Form.Item>

            <Form.Item name="reinsured" className="min-w-[180px] mb-0">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('claimsPayments.selectReinsured')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await reinsuredApi.filterReinsureds({
                    page,
                    pageSize,
                    searchText,
                  } as any);
                  return {
                    data: items.map((reinsured) => ({
                      label: reinsured.name,
                      value: reinsured.reinsuredId,
                    })),
                  };
                }}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                queryKey="claimsPaymentsReinsuredLazyLoadingSelect"
              />
            </Form.Item>

            <Form.Item name="broker" className="min-w-[180px] mb-0">
              <LazyLoadingSelect
                mode="multiple"
                placeholder={t('claimsPayments.selectBroker')}
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await brokerApi.filterBrokers({
                    page,
                    pageSize,
                    searchText,
                  });
                  return {
                    data: items.map((broker) => ({
                      label: broker.name,
                      value: broker.brokerId,
                    })),
                  };
                }}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                queryKey="claimsPaymentsBrokerLazyLoadingSelect"
              />
            </Form.Item>

            <Form.Item name="paymentStatus" className="min-w-[200px] mb-0">
              <SortedSelect
                mode="multiple"
                placeholder={t('claimsPayments.selectPaymentStatus')}
                options={paymentStatusOptions}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  ((option?.label ?? '') as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Space>
        )}
      </Form>

      <ExcelTable
        data={rowData}
        columnDefs={columnDefs}
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={total || 0}
        tableContainerClassName="h-[28rem]"
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        pageSizeOptions={[10, 20, 50, 100]}
        loading={isLoading}
      />
    </div>
  );
};

export default ClaimsPaymentsTable;
